import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Users,
  Music,
  CheckSquare,
  DollarSign,
  Calendar,
  Link,
  Unlink,
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Loader2,
  ExternalLink,
} from "lucide-react";
import toast from "react-hot-toast";

const PER_PAGE = 25;

// ─── helpers ────────────────────────────────────────────────────────────────

function attr(item, key) {
  return item?.attributes?.[key] ?? "—";
}

function formatDate(str) {
  if (!str) return "—";
  try {
    return new Date(str).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch {
    return str;
  }
}

function formatMoney(cents) {
  if (cents == null) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

// ─── sub-components ─────────────────────────────────────────────────────────

function EmptyState({ message = "No records found." }) {
  return (
    <div className="text-center py-16 text-slate-400">
      <AlertCircle className="w-8 h-8 mx-auto mb-3 opacity-40" />
      <p className="text-sm">{message}</p>
    </div>
  );
}

function Paginator({ meta, offset, onPrev, onNext }) {
  const total = meta?.total_count ?? 0;
  const count = meta?.count ?? 0;
  const from = offset + 1;
  const to = offset + count;
  const hasPrev = offset > 0;
  const hasNext = to < total;

  if (!total) return null;

  return (
    <div className="flex items-center justify-between pt-4 border-t border-slate-100 mt-4">
      <span className="text-xs text-slate-500">
        {from}–{to} of {total}
      </span>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={onPrev} disabled={!hasPrev}>
          <ChevronLeft className="w-3.5 h-3.5" />
        </Button>
        <Button variant="outline" size="sm" onClick={onNext} disabled={!hasNext}>
          <ChevronRight className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

function DataTable({ columns, rows, keyFn }) {
  if (!rows?.length) return <EmptyState />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-100">
            {columns.map((c) => (
              <th key={c.key} className="text-left py-2 px-3 font-medium text-slate-500 text-xs uppercase tracking-wide">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={keyFn(row)} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
              {columns.map((c) => (
                <td key={c.key} className="py-2.5 px-3 text-slate-700">
                  {c.render ? c.render(row) : c.get(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── tab panels ─────────────────────────────────────────────────────────────

function PeopleTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");

  const load = useCallback(async (off = 0, q = "") => {
    setLoading(true);
    try {
      const params = { offset: off, per_page: PER_PAGE };
      if (q) params.q = q;
      const res = await axios.get(`${API}/pco/people`, { params });
      setData(res.data);
      setOffset(off);
    } catch {
      toast.error("Failed to load people");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(0, search); }, [search]);

  const columns = [
    { key: "name", label: "Name", get: (r) => `${attr(r, "given_name")} ${attr(r, "last_name")}`.trim() },
    { key: "email", label: "Email", get: (r) => attr(r, "primary_email") },
    { key: "status", label: "Status", render: (r) => {
      const s = attr(r, "status");
      return <Badge variant={s === "active" ? "default" : "secondary"} className="text-xs capitalize">{s}</Badge>;
    }},
    { key: "membership", label: "Membership", get: (r) => attr(r, "membership") },
    { key: "created", label: "Added", get: (r) => formatDate(attr(r, "created_at")) },
  ];

  return (
    <div>
      <div className="flex gap-2 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-400" />
          <Input
            data-testid="pco-people-search"
            className="pl-8 h-8 text-sm"
            placeholder="Search by name…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && setSearch(query)}
          />
        </div>
        <Button size="sm" variant="outline" onClick={() => setSearch(query)}>Search</Button>
        {search && <Button size="sm" variant="ghost" onClick={() => { setQuery(""); setSearch(""); }}>Clear</Button>}
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
      ) : (
        <>
          <DataTable columns={columns} rows={data?.data ?? []} keyFn={(r) => r.id} />
          <Paginator
            meta={data?.meta}
            offset={offset}
            onPrev={() => load(offset - PER_PAGE, search)}
            onNext={() => load(offset + PER_PAGE, search)}
          />
        </>
      )}
    </div>
  );
}

function ServicesTab() {
  const [types, setTypes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [plans, setPlans] = useState(null);
  const [plansLoading, setPlansLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/pco/services`);
        setTypes(res.data);
      } catch {
        toast.error("Failed to load service types");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const loadPlans = async (typeId) => {
    setSelectedType(typeId);
    setPlansLoading(true);
    try {
      const res = await axios.get(`${API}/pco/services/${typeId}/plans`);
      setPlans(res.data);
    } catch {
      toast.error("Failed to load plans");
    } finally {
      setPlansLoading(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>;

  const typeList = types?.data ?? [];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Service types list */}
      <div className="md:col-span-1 space-y-1">
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Service Types</p>
        {!typeList.length && <EmptyState message="No service types found." />}
        {typeList.map((t) => (
          <button
            key={t.id}
            data-testid={`pco-service-type-${t.id}`}
            onClick={() => loadPlans(t.id)}
            className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${
              selectedType === t.id ? "bg-amber-50 text-amber-700 font-medium" : "hover:bg-slate-50 text-slate-700"
            }`}
          >
            {attr(t, "name")}
          </button>
        ))}
      </div>

      {/* Plans */}
      <div className="md:col-span-2">
        {!selectedType && <EmptyState message="Select a service type to view plans." />}
        {selectedType && plansLoading && (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
        )}
        {selectedType && !plansLoading && (
          <DataTable
            columns={[
              { key: "title", label: "Plan", get: (r) => attr(r, "title") || attr(r, "dates") || "Untitled" },
              { key: "dates", label: "Date", get: (r) => attr(r, "dates") },
              { key: "status", label: "Status", render: (r) => {
                const s = attr(r, "status") || "draft";
                return <Badge variant={s === "confirmed" ? "default" : "secondary"} className="capitalize text-xs">{s}</Badge>;
              }},
              { key: "series", label: "Series", get: (r) => attr(r, "series_title") },
            ]}
            rows={plans?.data ?? []}
            keyFn={(r) => r.id}
          />
        )}
      </div>
    </div>
  );
}

function CheckInsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);

  const load = useCallback(async (off = 0) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pco/checkins`, { params: { offset: off, per_page: PER_PAGE } });
      setData(res.data);
      setOffset(off);
    } catch {
      toast.error("Failed to load check-ins");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, []);

  return (
    <div>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
      ) : (
        <>
          <DataTable
            columns={[
              { key: "name", label: "Person", get: (r) => attr(r, "name") || attr(r, "full_name") },
              { key: "event", label: "Event", get: (r) => attr(r, "event_times_event_name") },
              { key: "kind", label: "Kind", render: (r) => {
                const k = attr(r, "kind");
                return <Badge variant="secondary" className="text-xs capitalize">{k}</Badge>;
              }},
              { key: "created", label: "Checked In", get: (r) => formatDate(attr(r, "created_at")) },
            ]}
            rows={data?.data ?? []}
            keyFn={(r) => r.id}
          />
          <Paginator
            meta={data?.meta}
            offset={offset}
            onPrev={() => load(offset - PER_PAGE)}
            onNext={() => load(offset + PER_PAGE)}
          />
        </>
      )}
    </div>
  );
}

function GroupsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);

  const load = useCallback(async (off = 0) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pco/groups`, { params: { offset: off, per_page: PER_PAGE } });
      setData(res.data);
      setOffset(off);
    } catch {
      toast.error("Failed to load groups");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, []);

  return (
    <div>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
      ) : (
        <>
          <DataTable
            columns={[
              { key: "name", label: "Group Name", get: (r) => attr(r, "name") },
              { key: "type", label: "Group Type", get: (r) => attr(r, "group_type_name") || attr(r, "schedule") },
              { key: "location", label: "Location", get: (r) => attr(r, "location") },
              { key: "members", label: "Members", get: (r) => attr(r, "memberships_count") },
              { key: "status", label: "Status", render: (r) => {
                const a = attr(r, "archived_at");
                return <Badge variant={a && a !== "—" ? "secondary" : "default"} className="text-xs">{a && a !== "—" ? "Archived" : "Active"}</Badge>;
              }},
            ]}
            rows={data?.data ?? []}
            keyFn={(r) => r.id}
          />
          <Paginator
            meta={data?.meta}
            offset={offset}
            onPrev={() => load(offset - PER_PAGE)}
            onNext={() => load(offset + PER_PAGE)}
          />
        </>
      )}
    </div>
  );
}

function GivingTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);

  const load = useCallback(async (off = 0) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pco/giving`, { params: { offset: off, per_page: PER_PAGE } });
      setData(res.data);
      setOffset(off);
    } catch {
      toast.error("Failed to load giving data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, []);

  const total = data?.data?.reduce((sum, r) => {
    const cents = r?.attributes?.amount_cents ?? 0;
    return sum + cents;
  }, 0) ?? 0;

  return (
    <div>
      {!loading && data?.data?.length > 0 && (
        <div className="flex items-center gap-3 mb-4 p-3 bg-green-50 rounded-lg border border-green-100">
          <DollarSign className="w-5 h-5 text-green-600" />
          <div>
            <p className="text-xs text-green-600 font-medium uppercase tracking-wide">Page Total</p>
            <p className="text-lg font-bold text-green-700">{formatMoney(total)}</p>
          </div>
        </div>
      )}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
      ) : (
        <>
          <DataTable
            columns={[
              { key: "date", label: "Date", get: (r) => formatDate(attr(r, "created_at")) },
              { key: "amount", label: "Amount", render: (r) => (
                <span className="font-medium text-green-700">{formatMoney(attr(r, "amount_cents"))}</span>
              )},
              { key: "method", label: "Method", render: (r) => {
                const m = attr(r, "payment_method");
                return <Badge variant="outline" className="text-xs capitalize">{m}</Badge>;
              }},
              { key: "refunded", label: "Refunded", render: (r) => {
                const ref = attr(r, "refunded");
                return ref === true ? <Badge variant="destructive" className="text-xs">Refunded</Badge> : null;
              }},
            ]}
            rows={data?.data ?? []}
            keyFn={(r) => r.id}
          />
          <Paginator
            meta={data?.meta}
            offset={offset}
            onPrev={() => load(offset - PER_PAGE)}
            onNext={() => load(offset + PER_PAGE)}
          />
        </>
      )}
    </div>
  );
}

function CalendarTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [filter, setFilter] = useState("future");

  const load = useCallback(async (off = 0, f = "future") => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pco/calendar`, { params: { offset: off, per_page: PER_PAGE, filter: f } });
      setData(res.data);
      setOffset(off);
    } catch {
      toast.error("Failed to load calendar events");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(0, filter); }, [filter]);

  return (
    <div>
      <div className="flex gap-2 mb-4">
        {["future", "past"].map((f) => (
          <Button
            key={f}
            size="sm"
            variant={filter === f ? "default" : "outline"}
            onClick={() => setFilter(f)}
            className={filter === f ? "bg-amber-500 hover:bg-amber-600 text-white border-0" : ""}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </Button>
        ))}
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
      ) : (
        <>
          <DataTable
            columns={[
              { key: "name", label: "Event", get: (r) => attr(r, "name") },
              { key: "start", label: "Starts", get: (r) => formatDate(attr(r, "starts_at")) },
              { key: "end", label: "Ends", get: (r) => formatDate(attr(r, "ends_at")) },
              { key: "location", label: "Location", get: (r) => attr(r, "location") },
              { key: "visible", label: "Visibility", render: (r) => {
                const v = attr(r, "visible_in_church_center");
                return <Badge variant={v ? "default" : "secondary"} className="text-xs">{v ? "Public" : "Private"}</Badge>;
              }},
            ]}
            rows={data?.data ?? []}
            keyFn={(r) => r.id}
          />
          <Paginator
            meta={data?.meta}
            offset={offset}
            onPrev={() => load(offset - PER_PAGE, filter)}
            onNext={() => load(offset + PER_PAGE, filter)}
          />
        </>
      )}
    </div>
  );
}

// ─── main page ───────────────────────────────────────────────────────────────

const MODULES = [
  { id: "people",   label: "People",    icon: Users,       description: "Member directory" },
  { id: "services", label: "Services",  icon: Music,       description: "Worship & service plans" },
  { id: "checkins", label: "Check-Ins", icon: CheckSquare, description: "Attendance records" },
  { id: "groups",   label: "Groups",    icon: Users,       description: "Small groups" },
  { id: "giving",   label: "Giving",    icon: DollarSign,  description: "Donations & contributions" },
  { id: "calendar", label: "Calendar",  icon: Calendar,    description: "Events & scheduling" },
];

export default function PlanningCenter({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [activeTab, setActiveTab] = useState("people");

  const loadStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pco/status`);
      setStatus(res.data);
    } catch {
      setStatus({ connected: false });
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // Planning Center is temporarily admin-only — redirect regular accounts away
  useEffect(() => {
    if (user && !user.is_super_admin) {
      navigate("/home");
    }
  }, [user, navigate]);

  // Handle OAuth redirect feedback
  useEffect(() => {
    const connected = searchParams.get("pco_connected");
    const err = searchParams.get("pco_error");
    if (connected === "true") {
      toast.success("Planning Center connected!");
      loadStatus();
      navigate("/planning-center", { replace: true });
    } else if (err) {
      const msgs = {
        access_denied: "Authorization was denied.",
        invalid_state: "Session expired. Please try again.",
        token_exchange_failed: "Failed to complete authentication.",
      };
      toast.error(msgs[err] || "Connection failed.");
      navigate("/planning-center", { replace: true });
    }
  }, [searchParams, loadStatus, navigate]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await axios.get(`${API}/pco/connect`);
      window.location.href = res.data.url;
    } catch {
      toast.error("Could not initiate Planning Center login.");
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect your Planning Center account?")) return;
    setDisconnecting(true);
    try {
      await axios.delete(`${API}/pco/disconnect`);
      setStatus({ connected: false });
      toast.success("Planning Center disconnected.");
    } catch {
      toast.error("Failed to disconnect.");
    } finally {
      setDisconnecting(false);
    }
  };

  if (user && !user.is_super_admin) return null;

  return (
    <Layout user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Planning Center</h1>
            <p className="text-sm text-slate-500 mt-1">
              View and manage your church data from Planning Center Online.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {status?.connected ? (
              <>
                <Badge className="bg-green-100 text-green-700 border-green-200 gap-1.5 px-2.5 py-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                  Connected
                </Badge>
                <Button
                  data-testid="pco-disconnect-btn"
                  variant="outline"
                  size="sm"
                  onClick={handleDisconnect}
                  disabled={disconnecting}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  {disconnecting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Unlink className="w-3.5 h-3.5 mr-1.5" />}
                  Disconnect
                </Button>
              </>
            ) : (
              <Button
                data-testid="pco-connect-btn"
                onClick={handleConnect}
                disabled={connecting}
                className="bg-amber-500 hover:bg-amber-600 text-white"
              >
                {connecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link className="w-4 h-4 mr-2" />}
                Connect Planning Center
              </Button>
            )}
          </div>
        </div>

        {/* Not connected state */}
        {status && !status.connected && (
          <Card className="border-dashed border-2 border-slate-200">
            <CardContent className="flex flex-col items-center justify-center py-16 text-center gap-4">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center">
                <ExternalLink className="w-7 h-7 text-slate-400" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800 mb-1">Connect to Planning Center</h3>
                <p className="text-sm text-slate-500 max-w-sm">
                  Link your Planning Center account to view People, Services, Check-Ins, Groups, Giving, and Calendar data directly in this app.
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2 w-full max-w-sm">
                {MODULES.map((m) => (
                  <div key={m.id} className="flex flex-col items-center gap-1.5 p-3 rounded-lg bg-slate-50 border border-slate-100">
                    <m.icon className="w-5 h-5 text-slate-400" />
                    <span className="text-xs text-slate-600 font-medium">{m.label}</span>
                  </div>
                ))}
              </div>
              <Button
                data-testid="pco-connect-btn-empty"
                onClick={handleConnect}
                disabled={connecting}
                className="bg-amber-500 hover:bg-amber-600 text-white mt-2"
                size="lg"
              >
                {connecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link className="w-4 h-4 mr-2" />}
                Connect Planning Center
              </Button>
              <p className="text-xs text-slate-400">
                You'll be redirected to Planning Center to authorize access.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Connected — data tabs */}
        {status?.connected && (
          <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="pco-tabs">
            <TabsList className="bg-slate-100 p-1 gap-1 flex-wrap h-auto">
              {MODULES.map((m) => (
                <TabsTrigger
                  key={m.id}
                  value={m.id}
                  data-testid={`pco-tab-${m.id}`}
                  className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-1.5 text-sm"
                >
                  <m.icon className="w-3.5 h-3.5" />
                  {m.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {MODULES.map((m) => (
              <TabsContent key={m.id} value={m.id} className="mt-4">
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base font-semibold flex items-center gap-2">
                        <m.icon className="w-4 h-4 text-amber-500" />
                        {m.label}
                        <span className="text-xs text-slate-400 font-normal">— {m.description}</span>
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {m.id === "people"   && <PeopleTab />}
                    {m.id === "services" && <ServicesTab />}
                    {m.id === "checkins" && <CheckInsTab />}
                    {m.id === "groups"   && <GroupsTab />}
                    {m.id === "giving"   && <GivingTab />}
                    {m.id === "calendar" && <CalendarTab />}
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>
        )}
      </div>
    </Layout>
  );
}
